import { AxiosError, AxiosResponse } from 'axios'
import { UseFormReturn, UseFormSetValue } from 'react-hook-form'
import { QueryObserverResult } from 'react-query'
import * as yup from 'yup'
import { ArraySchema, TestContext } from 'yup'

import agent from 'agent'
import { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { IntegrationResponse } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { trans, TranslationLanguage } from 'i18n/translate'
import {
  EndpointResponse,
  ObservabilityToolType,
  StatusCheckHeader,
  StatusCheckTeamExternalIntegration,
  TeamExternalIntegration,
  TeamExternalIntegrationAuthenticationStatus,
  TeamExternalIntegrationResponse
} from 'types/api/status-checks/status-check'
import { isPrivateEdition } from 'utils/app-config'
import { asyncRetryWithBackoff } from 'utils/retry'
import { appdynamicsBaseUrlRegex } from 'utils/status-checks/status-check-integrations-util-appdynamics'
import {
  awsIamArnRegex,
  awsAuthUrlRegex,
  getAWSAccountIdFromIamArn
} from 'utils/status-checks/status-check-integrations-util-aws'
import { dynatraceBaseUrlRegex } from 'utils/status-checks/status-check-integrations-util-dynatrace'
import { grafanaBaseUrlRegex } from 'utils/status-checks/status-check-integrations-util-grafana'
import { isValidUrl, parseUrl } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import {
  buildTeamExternalEndpointConfigurationRequest,
  computeResponseBodyEvaluation,
  convertToApiUrl,
  convertToBaseUrl,
  defaultStatusCheckEvaluationConfiguration,
  getDomainNameFromIntegration,
  getIntegrationTypeFromObservabilityToolTypeAndDomain,
  isValidStatusCodeOrRange,
  prepareHeaders,
  teamExternalIntegrationsObject
} from 'utils/team-external-integrations/team-external-integrations-util'
import { DOMAIN_NAME_INTEGRATIONS, ENDPOINT_URLS } from 'utils/third-party'
import { allowedIntegrationURLs } from 'utils/yup-additions'

/**
 * Types, Interfaces, Classes
 */
export type AWSDomain = {
  name: string
  integrationType: IntegrationType.AWS
}

export type ObservabilityToolDomain = {
  name: string
  integrationType?: IntegrationType
}

export type ObservabilityTool = {
  name: string
  type?: ObservabilityToolType
  integrationType?: IntegrationType
  domains?: ObservabilityToolDomain[]
  selectedDomain?: string
}

export enum AWSAuthenticationType {
  IAMRole = 'IAM Role',
  ServiceAccount = 'Service Account'
}

export enum AWSRoleCreationMethod {
  CloudFormation = 'Cloud Formation',
  Manual = 'Manual'
}

export interface AuthenticationFormValues {
  apiBaseUrl?: string
  authenticationUrl?: string
  awsAccessKeyID?: string
  awsAuthenticationType?: AWSAuthenticationType
  awsIamRoleArn?: string
  awsRoleCreationMethod?: AWSRoleCreationMethod
  awsSecretAccessKey?: string
  customDomainName?: string
  domain?: ObservabilityToolDomain
  externalIntegration?: TeamExternalIntegration
  headers: StatusCheckHeader[]
  isPrivateNetworkIntegration?: boolean
  monitorUrl?: string
  multipleRegions?: boolean
  observabilityTool: ObservabilityTool
  observabilityToolNickname?: string
}

/**
 * Constants
 */
export const INITIAL_AUTHENTICATION_STATUS_STATE = {
  isTesting: false,
  isSaving: false,
  isTestSuccess: false,
  isSaveSuccess: false,
  isTestError: false,
  isSaveError: false
}

export const OBSERVABILITY_TOOLS: ObservabilityTool[] = [
  {
    name: 'Datadog',
    type: ObservabilityToolType.DATADOG,
    integrationType: IntegrationType.Datadog, // Initial value
    domains: [
      { name: 'US1', integrationType: IntegrationType.Datadog },
      { name: 'US3', integrationType: IntegrationType.DatadogUS3 },
      { name: 'US5', integrationType: IntegrationType.DatadogUS5 },
      { name: 'EU', integrationType: IntegrationType.DatadogEU },
      { name: 'US1 FED', integrationType: IntegrationType.DatadogUS1Fed }
    ]
  },
  {
    name: 'PagerDuty',
    type: ObservabilityToolType.PAGERDUTY,
    integrationType: IntegrationType.Pagerduty,
    domains: []
  },
  {
    name: 'New Relic',
    type: ObservabilityToolType.NEWRELIC,
    integrationType: IntegrationType.Newrelic,
    domains: []
  },
  {
    name: 'Grafana Cloud',
    type: ObservabilityToolType.GRAFANA,
    integrationType: IntegrationType.Grafana,
    domains: []
  },
  {
    name: 'Dynatrace',
    type: ObservabilityToolType.DYNATRACE,
    integrationType: IntegrationType.Dynatrace,
    domains: []
  },
  {
    name: 'AppDynamics',
    type: ObservabilityToolType.APPDYNAMICS,
    integrationType: IntegrationType.Appdynamics,
    domains: []
  },
  {
    name: 'AWS',
    type: ObservabilityToolType.AWS,
    integrationType: IntegrationType.AWS,
    domains: []
  },
  {
    name: 'Custom',
    type: ObservabilityToolType.CUSTOM,
    integrationType: IntegrationType.Custom,
    domains: []
  }
]

/**
 * Necessary to distinguish between the error state of authenticating an observability tool and the error state of saving
 * an authenticated observability tool.
 */
export class ExternalIntegrationValidationError extends AxiosError {
  constructor(message: string) {
    super(message)
    this.name = 'ExternalIntegrationValidationError'
  }
}

/**
 * Necessary to distinguish between the error state of saving an authenticated observability tool and the error state of
 * authenticating an observability tool.
 */
export class ExternalIntegrationSaveError extends AxiosError {
  constructor(message: string) {
    super(message)
    this.name = 'ExternalIntegrationSaveError'
  }
}

export function getBinaryRadioOptions(language: TranslationLanguage) {
  return [
    {
      label: trans('no', {}, language),
      value: false
    },
    {
      label: trans('yes', {}, language),
      value: true
    }
  ]
}

/**
 * Validation Schemas
 */
function validateApiBaseUrl(value: string, integrationType: IntegrationType) {
  try {
    new URL(value)
  } catch (e) {
    return false
  }

  if (integrationType === IntegrationType.Grafana) {
    return grafanaBaseUrlRegex.test(value)
  }

  if (integrationType === IntegrationType.Dynatrace) {
    return dynatraceBaseUrlRegex.test(value)
  }

  if (integrationType === IntegrationType.Appdynamics) {
    return appdynamicsBaseUrlRegex.test(value)
  }

  return Boolean(
    allowedIntegrationURLs[
      integrationType ||
        IntegrationType.Custom ||
        IntegrationType.LoadGeneratorCustom
    ].find((u) => u.test(value))
  )
}

export function getAuthenticationFormValidationSchema(
  externalIntegrations: TeamExternalIntegration[]
) {
  // Adds a ".unique" method to the current yup instance
  yup.addMethod<ArraySchema<any, any, any[], any[]>>(
    yup.array,
    'unique',
    function (message, mapper = (a) => a) {
      return this.test(
        'unique',
        message,
        (list) => list.length === new Set(list.map(mapper)).size
      )
    }
  )

  const existingAWSIntegrationDomains = externalIntegrations.reduce(
    (acc, externalIntegration) => {
      if (externalIntegration.type === IntegrationType.AWS) {
        acc.push(externalIntegration.domain)
      }
      return acc
    },
    []
  )

  const existingCustomIntegrationNames = externalIntegrations
    .filter(
      (externalIntegration) =>
        externalIntegration.type === IntegrationType.Custom
    )
    .map((customExternalIntegration) => customExternalIntegration.name)

  const sanitizedUrls = externalIntegrations
    .filter((integration) => integration.isConfigured)
    .map((configuredIntegration) => configuredIntegration.url?.toLowerCase())

  return yup.object().shape({
    apiBaseUrl: yup
      .string()
      .trim()
      .when('observabilityTool', (observabilityTool, schema) => {
        if (
          observabilityTool?.type !== ObservabilityToolType.CUSTOM &&
          observabilityTool?.type !== ObservabilityToolType.AWS &&
          !isSelectedObservabilityToolAuthenticated(
            observabilityTool,
            externalIntegrations
          )
        ) {
          const { integrationType } = observabilityTool
          return schema
            .test(
              'is-valid-url',
              trans('forms.validation.enter_valid_url'),
              (value) => isValidUrl(value)
            )
            .test(
              'is-api-base-url-valid',
              trans('forms.validation.error.invalid_url'),
              (value) => validateApiBaseUrl(value, integrationType)
            )
            .test(
              'endpoint-already-used',
              trans('forms.validation.error.url_already_in_use'),
              (value) => {
                return !sanitizedUrls.includes(value?.trim().toLowerCase())
              }
            )
            .required(trans('forms.validation.enter_api_base_url'))
        }

        return schema
      }),
    // This form value serves the same function as the "apiBaseUrl" form value in the API authentication request payload
    // for the observability tool.  It replaces that value when authenticating a Custom observability tool. Since this
    // form is complex enough without a single key-value pair serving two functions, this value is stored and validated
    // separately for clarity.
    authenticationUrl: yup
      .string()
      .trim()
      .when('observabilityTool', {
        // for AWS - we just need to make sure the url is valid and includes the correct domain
        // it is okay for multiple AWS tools to have the same auth url
        // because the auth url is only used to run the authentication test
        // TODO: deprecate this validation check
        is: (observabilityTool: ObservabilityTool) =>
          observabilityTool?.type === ObservabilityToolType.AWS,
        then: (schema) =>
          schema
            .test(
              'is-valid-url',
              trans('forms.validation.enter_valid_url'),
              (value) => isValidUrl(value)
            )
            .test(
              'is-aws-authenticationUrl-valid',
              trans('forms.validation.error.invalid_url'),
              (value) => awsAuthUrlRegex.test(value)
            )
            .required(trans('forms.validation.enter_authentication_url'))
      })
      .when('observabilityTool', {
        is: (observabilityTool: ObservabilityTool) =>
          observabilityTool?.type === ObservabilityToolType.CUSTOM &&
          !isSelectedObservabilityToolAuthenticated(
            observabilityTool,
            externalIntegrations
          ),
        then: (schema) =>
          schema
            .test(
              'is-valid-url',
              trans('forms.validation.enter_valid_url'),
              (value) => isValidUrl(value)
            )
            .test(
              'endpoint-already-used',
              trans('forms.validation.error.url_already_in_use'),
              (value) => {
                return !sanitizedUrls.includes(value?.trim().toLowerCase())
              }
            )
            .required(trans('forms.validation.enter_authentication_url'))
      }),
    awsAccessKeyID: yup
      .string()
      .trim()
      .when(['observabilityTool', 'awsAuthenticationType'], {
        is: (
          observabilityTool: ObservabilityTool,
          awsAuthenticationType: AWSAuthenticationType
        ) =>
          observabilityTool?.type === ObservabilityToolType.AWS &&
          awsAuthenticationType === AWSAuthenticationType.ServiceAccount &&
          !isSelectedObservabilityToolAuthenticated(
            observabilityTool,
            externalIntegrations
          ),
        then: (schema) =>
          schema
            .min(1, trans('forms.validation.enter_aws_access_key_id'))
            .required(trans('forms.validation.enter_aws_access_key_id'))
      }),
    awsSecretAccessKey: yup
      .string()
      .trim()
      .when(['observabilityTool', 'awsAuthenticationType'], {
        is: (
          observabilityTool: ObservabilityTool,
          awsAuthenticationType: AWSAuthenticationType
        ) =>
          observabilityTool?.type === ObservabilityToolType.AWS &&
          awsAuthenticationType === AWSAuthenticationType.ServiceAccount &&
          !isSelectedObservabilityToolAuthenticated(
            observabilityTool,
            externalIntegrations
          ),
        then: (schema) =>
          schema
            .min(1, trans('forms.validation.enter_aws_secret_access_key'))
            .required(trans('forms.validation.enter_aws_secret_access_key'))
      }),
    awsIamRoleArn: yup
      .string()
      .when(
        ['observabilityTool', 'awsAuthenticationType', 'externalIntegration'],
        {
          is: (
            observabilityTool: ObservabilityTool,
            awsAuthenticationType: AWSAuthenticationType
          ) =>
            observabilityTool.type === ObservabilityToolType.AWS &&
            awsAuthenticationType === AWSAuthenticationType.IAMRole &&
            !isSelectedObservabilityToolAuthenticated(
              observabilityTool,
              externalIntegrations
            ),
          then: (schema) =>
            schema
              .required(trans('forms.validation.error.empty_aws_iam_role_arn'))
              .test(
                'is-valid-arn',
                trans('forms.validation.enter_aws_iam_role_arn'),
                (value) => Boolean(value) && awsIamArnRegex.test(value)
              )
              .test(
                'is-arn-account-id-equal-to-existing-authentication-account-id',
                trans(
                  'forms.validation.error.aws_iam_role_arn.account_id_mismatch'
                ),
                (value: string, context: TestContext) => {
                  const newAccountId = getAWSAccountIdFromIamArn(value)
                  const existingAccountId = context.parent.domain?.name

                  // Only check the account IDs if we are editing an existing AWS authentication
                  if (existingAccountId) {
                    return newAccountId === existingAccountId
                  }

                  return true
                }
              )
        }
      ),
    awsAuthenticationType: yup
      .string()
      .oneOf(Object.values(AWSAuthenticationType)),
    awsRoleCreationMethod: yup
      .string()
      .oneOf(Object.values(AWSRoleCreationMethod)),
    customDomainName: yup
      .string()
      .nullable()
      .when('multipleRegions', {
        is: true,
        then: (schema) =>
          schema
            .min(1, trans('forms.validation.error.empty_domain_name'))
            .required(trans('forms.validation.enter_domain_name'))
      }),
    domain: yup
      .object()
      .when(
        ['observabilityTool', 'externalIntegration', 'awsAuthenticationType'],
        {
          is: (
            observabilityTool: ObservabilityTool,
            externalIntegration: TeamExternalIntegration,
            awsAuthenticationType: AWSAuthenticationType
          ) =>
            observabilityTool &&
            observabilityTool?.type === ObservabilityToolType.AWS &&
            !externalIntegration?.isConfigured &&
            externalIntegration?.lastAuthenticationStatus !==
              TeamExternalIntegrationAuthenticationStatus.Authenticated &&
            awsAuthenticationType === AWSAuthenticationType.ServiceAccount,
          then: (schema) => {
            return schema.shape({
              name: yup.string().when('integrationType', {
                is: (integrationType: IntegrationType) =>
                  integrationType === IntegrationType.AWS,
                then: (schema) =>
                  schema
                    .required(trans('forms.validation.enter_aws_account_id'))
                    .length(
                      12,
                      trans(
                        'forms.validation.enter_aws_account_id.error.length'
                      )
                    )
                    .notOneOf(
                      existingAWSIntegrationDomains,
                      trans(
                        'forms.validation.enter_aws_account_id.error.already_in_use'
                      )
                    )
                    .test(
                      'is-valid-account-number',
                      'Account ID cannot contain letters or special characters',
                      (value, context) => {
                        if (
                          context.parent.integrationType === IntegrationType.AWS
                        ) {
                          return String(parseInt(value)).length === value.length
                        }

                        return true
                      }
                    )
              }),
              integrationType: yup
                .string()
                .oneOf(Object.values(IntegrationType))
            })
          }
        }
      )
      .when('observabilityTool', {
        is: (observabilityTool: ObservabilityTool) =>
          observabilityTool &&
          observabilityTool?.domains?.length > 0 &&
          observabilityTool?.type !== ObservabilityToolType.AWS,
        then: (schema) =>
          schema.shape({
            name: yup
              .string()
              .required(trans('forms.validation.select_domain')),
            integrationType: yup.string().oneOf(Object.values(IntegrationType))
          })
      }),
    externalIntegration: yup.mixed<TeamExternalIntegration>(),
    headers: yup
      .array()
      .of(
        yup.object({
          key: yup.string(),
          value: yup.string(),
          label: yup.string().optional()
        })
      ) // @ts-ignore TODO: update Yup
      .unique(
        trans('forms.validation.error.duplicate_header_key'),
        (a) => a.key
      )
      .when(
        'observabilityTool',
        (observabilityTool: ObservabilityTool, schema) => {
          // Retrieve the matching external integration for header key-value validation
          const externalIntegration =
            getExternalIntegrationForObservabilityTool(
              observabilityTool,
              externalIntegrations
            )

          // Modify the schema if the observability tool has a matching external integration and is not authenticated
          return observabilityTool &&
            observabilityTool?.type !== ObservabilityToolType.CUSTOM &&
            externalIntegration
            ? schema.of(
                yup.object({
                  key: yup
                    .string()
                    .min(1, trans('forms.validation.error.empty_header_key'))
                    .oneOf(externalIntegration.headers.map((h) => h.key)) // Ensures the correct header key values for standard integrations
                    .required(trans('forms.validation.enter_header_key')),
                  value: yup
                    .string()
                    .min(1, trans('forms.validation.error.empty_header_value'))
                    .required(trans('forms.validation.enter_header_value')),
                  label: yup.string().optional()
                })
              )
            : schema
        }
      ),
    isPrivateNetworkIntegration: yup.boolean(),
    multipleRegions: yup.boolean(),
    observabilityTool: yup
      .mixed<ObservabilityTool>()
      .required(trans('forms.validation.select_observability_tool')),
    observabilityToolNickname: yup.string().when('observabilityTool', {
      is: (observabilityTool: ObservabilityTool) =>
        observabilityTool?.type === ObservabilityToolType.CUSTOM &&
        !isSelectedObservabilityToolAuthenticated(
          observabilityTool,
          externalIntegrations
        ),
      then: (schema) =>
        schema
          .min(1, trans('forms.validation.error.empty_observability_tool_name'))
          .notOneOf(
            existingCustomIntegrationNames,
            trans('forms.validation.error.name_already_exists')
          )
          .required(trans('forms.validation.enter_observability_tool_name'))
    })
  })
}

/**
 * Utility Functions
 */
export function getIntegrationUrl(integration: TeamExternalIntegration) {
  const isDomainNameIntegration = DOMAIN_NAME_INTEGRATIONS.includes(
    integration.type
  )
  const shouldUseIntegrationUrl =
    integration.type === IntegrationType.Custom ||
    integration.type === IntegrationType.AWS

  if (shouldUseIntegrationUrl) {
    return integration.url
  } else if (isDomainNameIntegration) {
    return convertToBaseUrl(integration)
  } else {
    return ENDPOINT_URLS[integration.type]
  }
}

export function getAWSIntegrationSpecificFormValues(
  integration: TeamExternalIntegration | StatusCheckTeamExternalIntegration
) {
  if (integration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN) {
    return {
      awsIamRoleArn: integration.integrationSpecificValues.AWS_IAM_ROLE_ARN
    }
  }

  return {
    awsAccessKeyID: integration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID,
    awsSecretAccessKey:
      integration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY
  }
}

export function getAWSIntegrationSpecificValues(
  integration: TeamExternalIntegration | StatusCheckTeamExternalIntegration
) {
  if (integration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN) {
    return {
      AWS_IAM_ROLE_ARN: integration.integrationSpecificValues.AWS_IAM_ROLE_ARN
    }
  }

  return {
    AWS_ACCESS_KEY_ID:
      integration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID,
    AWS_SECRET_ACCESS_KEY:
      integration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY
  }
}

/**
 * Builds default Edit Authentication form values for editing a Team External Integration
 */
export function getDefaultAuthenticationFormValues(
  externalIntegration?: TeamExternalIntegration
): AuthenticationFormValues {
  if (!externalIntegration) {
    return {
      apiBaseUrl: '',
      authenticationUrl: '',
      awsAccessKeyID: '',
      awsIamRoleArn: '',
      awsSecretAccessKey: '',
      awsAuthenticationType: AWSAuthenticationType.IAMRole,
      awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
      customDomainName: '',
      domain: { name: '' },
      externalIntegration: null,
      headers: [],
      isPrivateNetworkIntegration: false,
      multipleRegions: false,
      observabilityTool: { name: '' },
      observabilityToolNickname: ''
    }
  }

  const {
    domain,
    isPrivateNetworkIntegration,
    name,
    observabilityToolType,
    type: integrationType,
    url
  } = externalIntegration

  const observabilityToolName =
    observabilityToolType === ObservabilityToolType.CUSTOM
      ? name
      : OBSERVABILITY_TOOLS.find(
          (observabilityTool) =>
            observabilityTool.type === observabilityToolType
        ).name

  const observabilityTool: ObservabilityTool = {
    name: observabilityToolName,
    type: observabilityToolType,
    integrationType
  }

  const domainName = getDomainNameFromIntegration(externalIntegration)

  // Configures any new custom domains to the observability tool so they are immediately available
  const domains =
    observabilityTool.type === ObservabilityToolType.CUSTOM && domainName
      ? [
          {
            name: domain,
            integrationType: IntegrationType.Custom
          }
        ]
      : OBSERVABILITY_TOOLS.find(
          (staticObservabilityTool) =>
            staticObservabilityTool.type === observabilityTool.type
        ).domains

  observabilityTool.domains = domains

  const customDomainName =
    observabilityToolType === ObservabilityToolType.CUSTOM && domains.length > 0
      ? domain
      : ''

  const awsAuthenticationType = externalIntegration?.integrationSpecificValues
    ?.AWS_IAM_ROLE_ARN
    ? AWSAuthenticationType.IAMRole
    : AWSAuthenticationType.ServiceAccount
  const awsRoleCreationMethod = AWSRoleCreationMethod.CloudFormation

  return {
    observabilityTool,
    observabilityToolNickname:
      observabilityTool.type === ObservabilityToolType.CUSTOM
        ? observabilityToolName
        : '',
    domain: domainName
      ? {
          name: domainName,
          integrationType
        }
      : { name: '' },
    apiBaseUrl:
      observabilityToolType !== ObservabilityToolType.CUSTOM ? url : '',
    awsAuthenticationType,
    awsRoleCreationMethod,
    awsIamRoleArn:
      externalIntegration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN || '',
    multipleRegions: Boolean(customDomainName),
    isPrivateNetworkIntegration: isPrivateNetworkIntegration || false,
    customDomainName,
    authenticationUrl:
      observabilityToolType === ObservabilityToolType.CUSTOM ||
      observabilityToolType === ObservabilityToolType.AWS
        ? url
        : '',
    headers: externalIntegration.headers || [],
    externalIntegration,
    ...(observabilityToolType === ObservabilityToolType.AWS &&
      getAWSIntegrationSpecificFormValues(externalIntegration))
  }
}

function getAwsDomainFromService(
  domains: AWSDomain[],
  selectedAwsAccountId?: string
): ObservabilityToolDomain {
  const defaultDomain: AWSDomain = {
    name: '',
    integrationType: IntegrationType.AWS
  }

  // No domains available, return the default domain
  if (domains.length === 0) {
    return defaultDomain
  }

  // If the AWS account id is not available, return the first domain
  if (selectedAwsAccountId) {
    return (
      domains.find((d) => d.name === selectedAwsAccountId) || {
        name: selectedAwsAccountId,
        integrationType: IntegrationType.AWS
      }
    )
  }

  return {
    ...defaultDomain,
    ...domains[0]
  }
}

export function getAWSOnboardingAuthenticationDefaultValues(
  externalIntegrations: TeamExternalIntegration[],
  selectedAwsAccountId?: string
): AuthenticationFormValues {
  const domains = getAWSDomains(externalIntegrations)
  const domain = getAwsDomainFromService(domains, selectedAwsAccountId)

  const observabilityTool: ObservabilityTool = {
    name: 'AWS',
    type: ObservabilityToolType.AWS,
    integrationType: IntegrationType.AWS,
    domains,
    selectedDomain: domain.name
  }

  const externalIntegration = externalIntegrations.find((integration) => {
    return (
      integration.type === IntegrationType.AWS &&
      (integration.domain ?? '' === domain.name)
    )
  })

  return {
    apiBaseUrl: '',
    authenticationUrl: externalIntegration?.url || '',
    awsAccessKeyID:
      externalIntegration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID || '',
    awsIamRoleArn:
      externalIntegration?.integrationSpecificValues?.AWS_IAM_ROLE_ARN || '',
    awsSecretAccessKey:
      externalIntegration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY ||
      '',
    awsAuthenticationType: isPrivateEdition()
      ? AWSAuthenticationType.ServiceAccount
      : AWSAuthenticationType.IAMRole,
    awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
    customDomainName: '',
    domain,
    externalIntegration: externalIntegration || null,
    headers: [],
    isPrivateNetworkIntegration: false,
    multipleRegions: false,
    observabilityTool,
    observabilityToolNickname: ''
  }
}

/**
 * Gets the external Integration that matches the passed in Observability Tool
 */
export function getExternalIntegrationForObservabilityTool(
  observabilityTool: ObservabilityTool,
  externalIntegrations: TeamExternalIntegration[],
  isCreatingNewAuthentication = false
): TeamExternalIntegration {
  if (observabilityTool?.type === ObservabilityToolType.CUSTOM) {
    // Custom external integrations are match to the selected observability tool by Name
    return externalIntegrations?.find(
      (integration) => integration.name === observabilityTool.name
    )
  }

  if (observabilityTool?.type === ObservabilityToolType.AWS) {
    // when user is creating a new AWS account (aka domain)
    // they either won't have entered a domain value yet
    // or they have entered one that doesn't exist in externalIntegration yet
    // either way - return the first instance of the AWS that has
    // no `domain` value - this is a "clean" instance we hardcode locally
    const existingAWSExternalIntegration = externalIntegrations?.find(
      (integration) =>
        integration.observabilityToolType === observabilityTool?.type &&
        integration.domain === observabilityTool?.selectedDomain
    )

    if (
      !observabilityTool.selectedDomain ||
      isCreatingNewAuthentication ||
      (observabilityTool.selectedDomain && !existingAWSExternalIntegration)
    ) {
      return externalIntegrations?.find(
        (integration) =>
          integration.type === observabilityTool.integrationType &&
          !integration.domain
      )
    }

    return externalIntegrations?.find(
      (integration) =>
        integration.observabilityToolType === observabilityTool?.type &&
        integration.domain === observabilityTool?.selectedDomain
    )
  }

  // Non-custom external integrations are matched to the selected observability tool by IntegrationType
  return externalIntegrations?.find(
    (integration) => integration.type === observabilityTool.integrationType
  )
}

/**
 * Compares the currently selected Observability Tool against an existing integrations list,
 * returning true if the integration exists and is authenticated.
 */
export function isSelectedObservabilityToolAuthenticated(
  observabilityTool: ObservabilityTool,
  externalIntegrations: TeamExternalIntegration[]
): boolean {
  const externalIntegration = getExternalIntegrationForObservabilityTool(
    observabilityTool,
    externalIntegrations
  )

  return (
    externalIntegration?.lastAuthenticationStatus ===
    TeamExternalIntegrationAuthenticationStatus.Authenticated
  )
}

export function setCustomObservabilityToolSpecificValues(
  observabilityTool: ObservabilityTool,
  integration: TeamExternalIntegration,
  setValue: UseFormSetValue<StatusCheckFormValues | AuthenticationFormValues>
) {
  if (integration.isConfigured) {
    setValue('observabilityToolNickname', observabilityTool.name)
  } else {
    // resetField does not always work here for some reason
    setValue('observabilityToolNickname', '')
  }
}

export function setAWSObservabilityToolSpecificValues(
  integration: TeamExternalIntegration,
  formValues: StatusCheckFormValues | AuthenticationFormValues,
  setValue: UseFormSetValue<StatusCheckFormValues | AuthenticationFormValues>
) {
  const isServiceAccountIntegration =
    Boolean(integration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID) ||
    Boolean(integration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY)

  // Set the various AWS authentication form values to the values from the integration if it is already configured
  if (integration?.isConfigured) {
    if (isServiceAccountIntegration) {
      setValue(
        'awsAccessKeyID',
        integration.integrationSpecificValues.AWS_ACCESS_KEY_ID
      )
      setValue(
        'awsSecretAccessKey',
        integration.integrationSpecificValues.AWS_SECRET_ACCESS_KEY
      )
    } else {
      setValue(
        'awsIamRoleArn',
        integration.integrationSpecificValues.AWS_IAM_ROLE_ARN
      )
    }

    setValue('observabilityTool.selectedDomain', integration.domain)
  } else {
    // Set the AWS authentication form values to either empty or existing form values (in the case of creating a new AWS authentication only)
    setValue('awsAccessKeyID', formValues.awsAccessKeyID || '')
    setValue('awsSecretAccessKey', formValues.awsSecretAccessKey || '')
    setValue('awsIamRoleArn', formValues.awsIamRoleArn || '')
  }

  // Only set the AWS authentication type and role creation method if they are not already set (in the case of creating/editing an AWS authentication only)
  if (!formValues.awsAuthenticationType && !formValues.awsRoleCreationMethod) {
    if (isServiceAccountIntegration) {
      setValue('awsAuthenticationType', AWSAuthenticationType.ServiceAccount)
    } else {
      setValue('awsAuthenticationType', AWSAuthenticationType.IAMRole)
      setValue('awsRoleCreationMethod', AWSRoleCreationMethod.CloudFormation)
    }
  }
}

/**
 * Handles building and setting relevant form values when selecting a new observability tool and/or domain selection.
 */
export function setObservabilityToolAndDomain(
  observabilityTool: ObservabilityTool,
  domain: ObservabilityToolDomain,
  integration: TeamExternalIntegration,
  form: UseFormReturn<StatusCheckFormValues | AuthenticationFormValues>
) {
  const { clearErrors, getValues, resetField, setValue } = form

  const newResponseBodyEvaluation = computeResponseBodyEvaluation(integration)

  if (newResponseBodyEvaluation) {
    setValue('evaluationConfiguration', {
      ...getValues('evaluationConfiguration'),
      responseBodyEvaluation: newResponseBodyEvaluation
    })
    setValue('predicates', newResponseBodyEvaluation.predicates)
  } else {
    setValue(
      'evaluationConfiguration',
      defaultStatusCheckEvaluationConfiguration
    )

    resetField('predicates')
  }

  if (domain?.name) {
    setValue('domain', domain)
  } else if (observabilityTool.domains?.length > 0) {
    // set domain value to first domain in list
    setValue('domain', observabilityTool.domains[0])
  } else {
    // reset domain value
    resetField('domain')
  }

  // For custom observability tools, set the observability tool nickname to the integration name if the integration is configured.
  // For all other observability tools, reset the observability tool nickname to an empty string.
  if (
    observabilityTool.type === ObservabilityToolType.CUSTOM &&
    integration.isConfigured
  ) {
    setValue('observabilityToolNickname', observabilityTool.name)
  } else {
    // resetField does not always work here for some reason
    setValue('observabilityToolNickname', '')
  }

  if (
    observabilityTool.type !== ObservabilityToolType.CUSTOM &&
    observabilityTool.type !== ObservabilityToolType.AWS
  ) {
    setValue('apiBaseUrl', integration.url)
    resetField('authenticationUrl')
  } else {
    setValue('authenticationUrl', integration.url)
    resetField('apiBaseUrl')
  }

  setValue('statusCheckName', '')
  setValue('externalIntegration', integration)
  setValue(
    'isPrivateNetworkIntegration',
    integration.isPrivateNetworkIntegration
  )

  setValue('headers', integration.headers)
  setValue('observabilityTool', observabilityTool)

  if (observabilityTool.type === ObservabilityToolType.AWS) {
    setAWSObservabilityToolSpecificValues(integration, getValues(), setValue)
  }

  resetField('monitorUrl')

  clearErrors()
}

export function getAWSDomains(
  externalIntegrations: TeamExternalIntegration[]
): AWSDomain[] {
  return externalIntegrations
    .filter(
      (externalIntegration) =>
        externalIntegration.observabilityToolType === ObservabilityToolType.AWS
    )
    .map((awsIntegration) => ({
      name: awsIntegration.domain,
      integrationType: IntegrationType.AWS
    }))
}

// Configures any new custom domains to the observability tool so they are immediately available
function getObservabilityToolDomains(
  observabilityTool: ObservabilityTool,
  customDomainName: string,
  AWSDomains: AWSDomain[],
  responseData: TeamExternalIntegrationResponse
) {
  if (
    observabilityTool.type === ObservabilityToolType.CUSTOM &&
    customDomainName
  ) {
    return [
      {
        name: responseData.domain,
        integrationType: IntegrationType.Custom
      }
    ]
  }

  if (observabilityTool.type === ObservabilityToolType.AWS) {
    return AWSDomains
  }

  return OBSERVABILITY_TOOLS.find(
    (staticObservabilityTool) =>
      staticObservabilityTool.type === observabilityTool.type
  ).domains
}

/**
 * Rebuilds the base form values using the response data received from authenticating a new Team Authentication and resets
 * the form state with the new Team Authentication already selected.
 */
export function refreshFormAfterObservabilityToolAuthentication(
  responseData: TeamExternalIntegrationResponse,
  integration: TeamExternalIntegration,
  externalIntegrations: TeamExternalIntegration[],
  form: UseFormReturn<StatusCheckFormValues | AuthenticationFormValues>
) {
  const { getValues, reset } = form
  const {
    customDomainName,
    domain,
    observabilityTool,
    isPrivateNetworkIntegration
  } = getValues()

  const isCustomIntegration = integration.type === IntegrationType.Custom

  // add back the new domain that just got added
  const AWSDomains = getAWSDomains(externalIntegrations)

  AWSDomains.push({
    name: responseData.domain,
    integrationType: IntegrationType.AWS
  })

  const domains = getObservabilityToolDomains(
    observabilityTool,
    customDomainName,
    AWSDomains,
    responseData
  )

  // Build newly created observability tool
  const newObservabilityTool: ObservabilityTool = {
    ...observabilityTool,
    name: integration.name,
    domains
  }

  if (integration.observabilityToolType === ObservabilityToolType.AWS) {
    newObservabilityTool.selectedDomain = responseData.domain
    domain.name = responseData.domain
  }

  // Find new external integration
  const newExternalIntegration = getExternalIntegrationForObservabilityTool(
    newObservabilityTool,
    externalIntegrations
  )

  // Headers in the integration are returned as an object from the API. We need to map them to an array of keys and values
  const headers = Object.keys(responseData.headers).map((key) => ({
    key,
    value: responseData.headers[key]
  }))

  // Build newly configured integration from response data and retrieved integration object from context
  const newIntegration = {
    ...responseData,
    ...(!isCustomIntegration ? newExternalIntegration : {}), // Custom integrations are not returned right away
    headers,
    isPrivateNetworkIntegration,
    isConfigured: true
  }

  // Reset form
  reset()

  // Select newly created observability tool
  setObservabilityToolAndDomain(
    newObservabilityTool,
    domain,
    newIntegration,
    form
  )
}

/**
 * Gets translatable AWS authentication types
 */
export function getAWSAuthenticationTypes(
  language: TranslationLanguage = 'en'
) {
  return [
    {
      label: trans(
        'status_checks.authentication.aws.authentication_type.iam_role',
        {},
        language
      ),
      value: AWSAuthenticationType.IAMRole
    },
    {
      label: trans(
        'status_checks.authentication.aws.authentication_type.service_account',
        {},
        language
      ),
      value: AWSAuthenticationType.ServiceAccount
    }
  ]
}

/**
 * Gets translatable AWS role creation methods
 */
export function getAWSRoleCreationMethods(
  language: TranslationLanguage = 'en'
) {
  return [
    {
      label: trans(
        'status_checks.authentication.aws.role_creation_method.cloud_formation',
        {},
        language
      ),
      value: AWSRoleCreationMethod.CloudFormation
    },
    {
      label: trans(
        'status_checks.authentication.aws.role_creation_method.manual',
        {},
        language
      ),
      value: AWSRoleCreationMethod.Manual
    }
  ]
}

/**
 * Filters the base list of all observability tools to exclude those without a saved external integration
 */
export function getUnsavedObservabilityTools(
  unsavedExternalIntegrations: TeamExternalIntegration[],
  AWSDomains: AWSDomain[]
) {
  const unsavedExternalIntegrationsTypes = unsavedExternalIntegrations.map(
    (externalIntegration) => externalIntegration.type
  )

  return [...OBSERVABILITY_TOOLS].reduce((acc, observabilityTool) => {
    // Create a copy of the observability tool to avoid mutating the original
    const newObservabilityTool = { ...observabilityTool }

    // Check if the observability tool has a corresponding unsaved external integration
    const isNotSaved = unsavedExternalIntegrationsTypes.includes(
      newObservabilityTool.integrationType
    )

    if (newObservabilityTool.domains.length > 0) {
      // Filter out any domains that don't have a corresponding unsaved external integration
      newObservabilityTool.domains = observabilityTool.domains.filter(
        (domain) => {
          return unsavedExternalIntegrationsTypes.includes(
            domain.integrationType
          )
        }
      )
    }

    // If the observability tool is not saved or has domains, add it to the list
    if (isNotSaved || newObservabilityTool.domains.length > 0) {
      if (observabilityTool.name === 'AWS') {
        newObservabilityTool.domains = AWSDomains
      }

      acc.push(newObservabilityTool)
    }

    return acc
  }, [])
}

/**
 * This is a close duplicate of the function found in team-external-integrations-util.ts. It became necessary to alter the
 * invocations of onSuccess and onError in order to properly handle the response from the private network endpoint in the UI.
 */
export function handlePrivateNetworkInvocationResponse(
  invocationResponse,
  onSuccess,
  onError: (endpointResponse: EndpointResponse) => void
) {
  const { errorMessage, testEndpointResponse } = invocationResponse.data

  if (invocationResponse.status === 204) {
    //status is No Content - not ready yet
    return false
  } else if (errorMessage) {
    onError({
      responseBody: errorMessage,
      httpStatusCode: testEndpointResponse?.httpStatusCode
    })

    return true
  }

  if (
    isValidStatusCodeOrRange(testEndpointResponse?.httpStatusCode?.toString())
  ) {
    onSuccess(testEndpointResponse)
  } else {
    onError(testEndpointResponse)
  }

  return true
}

/**
 * This is a close duplicate of the function found in team-external-integrations-util.ts. It became necessary to alter the
 * invocations of onSuccess and onError in order to properly handle the response from the private network endpoint in the UI.
 */
export async function testTeamExternalIntegrationEndpointConfiguration(
  integration: TeamExternalIntegration,
  teamId: string,
  onSuccess: (endpointResponse: EndpointResponse) => void,
  onError: (endpointResponse: EndpointResponse) => void,
  statusCheckId?: string,
  authenticate?: boolean
) {
  const teamIntegrationType =
    getIntegrationTypeFromObservabilityToolTypeAndDomain(
      integration.observabilityToolType,
      integration.domain
    )

  const integrationUrl =
    (await convertToApiUrl(integration, teamId)) ||
    teamExternalIntegrationsObject?.[teamIntegrationType]?.authUrl

  if (parseUrl(integrationUrl) === false) {
    onError({
      responseBody: `integration url (${integrationUrl}) is not a valid url`
    } as EndpointResponse)
    return
  }

  const requestData = buildTeamExternalEndpointConfigurationRequest(
    integration,
    integrationUrl,
    statusCheckId,
    authenticate
  )

  if (integration.isPrivateNetworkIntegration) {
    const { data } = await agent.post(
      `integration-invocations?teamId=${teamId}`,
      requestData
    )

    const invocationId = data?.invocationId
    await asyncRetryWithBackoff(
      500,
      20 * 1000,
      async () => {
        let invocationResponse

        try {
          invocationResponse = await agent.post(
            `integration-invocations/${invocationId}/run-test-endpoint?teamId=${teamId}`,
            null
          )
        } catch (err) {
          if (err.name === 'CanceledError') {
            onError({ responseBody: 'Canceled' })
          } else {
            onError(null)
            throw new ExternalIntegrationValidationError(err.message)
          }
          return true
        }

        return handlePrivateNetworkInvocationResponse(
          invocationResponse,
          onSuccess,
          onError
        )
      },
      () => {},
      () => {
        onError({})
      }
    )
  } else {
    const { data } = await agent.post(
      `status-checks/run-test-endpoint?teamId=${teamId}`,
      requestData
    )

    if (
      isValidStatusCodeOrRange(
        data?.testEndpointResponse?.httpStatusCode?.toString()
      )
    ) {
      onSuccess(data?.testEndpointResponse)
    } else {
      onError(data?.testEndpointResponse)
    }
  }
}

function getSaveAuthenticatedObservabilityToolRequestMethodAndUrl(
  integration: TeamExternalIntegration,
  domainName: string,
  currentTeamId: string,
  isEdit: boolean
) {
  if (isEdit) {
    return {
      requestMethod: 'put',
      url: `external-integrations/status-check?teamId=${currentTeamId}&name=${
        integration.name
      }&observabilityToolType=${integration.observabilityToolType}${
        domainName ? `&domain=${domainName}` : ''
      }`
    }
  }

  return {
    requestMethod: 'post',
    url: `external-integrations/status-check?teamId=${currentTeamId}&observabilityToolType=${
      integration.observabilityToolType
    }${domainName ? `&domain=${domainName}` : ''}`
  }
}

/**
 * This is a close duplicate of the function found in team-external-integrations-util.ts. It became necessary to alter the
 * return value in order to properly handle the success and error states of this response in the UI.
 */
export async function saveAuthenticatedObservabilityTool(
  formValues: StatusCheckFormValues | AuthenticationFormValues,
  integration: TeamExternalIntegration,
  isEdit: boolean,
  reFetchExternalIntegrations: () => Promise<
    QueryObserverResult<IntegrationResponse, AxiosError>
  >,
  currentTeamId: string
): Promise<AxiosResponse<TeamExternalIntegrationResponse>> {
  const { domain, customDomainName, multipleRegions } = formValues

  const isCustomIntegration = integration.type === IntegrationType.Custom
  const isAWS = integration.type === IntegrationType.AWS

  const staticDomainName = domain.name === 'US1' ? null : domain.name

  const domainName =
    isCustomIntegration && multipleRegions ? customDomainName : staticDomainName

  const requestBody = {
    url: getIntegrationUrl(integration),
    headers: prepareHeaders(integration),
    name: integration.name,
    privateNetwork: integration.isPrivateNetworkIntegration,
    lastAuthenticationStatus:
      TeamExternalIntegrationAuthenticationStatus.Authenticated,
    ...(isAWS && {
      integrationSpecificValues: getAWSIntegrationSpecificValues(integration)
    })
  }

  const { requestMethod, url } =
    getSaveAuthenticatedObservabilityToolRequestMethodAndUrl(
      integration,
      domainName,
      currentTeamId,
      isEdit
    )

  const response = await agent[requestMethod](url, requestBody)

  // Refresh integrations list
  reFetchExternalIntegrations()

  return response
}

/**
 * `getAWSOnboardingAuthenticationDefaultValues`
 * defaults to the first domain if one exists.
 * There are times we may want a blank slate and
 * not want to default to an existing domain.
 */
type AWSDefaultValues = Pick<
  AuthenticationFormValues,
  | 'awsAccessKeyID'
  | 'awsIamRoleArn'
  | 'awsSecretAccessKey'
  | 'awsAuthenticationType'
  | 'awsRoleCreationMethod'
  | 'domain'
  | 'externalIntegration'
  | 'observabilityTool'
>
export function getAWSDefaultValuesForReset(
  externalIntegrations: TeamExternalIntegration[]
): AuthenticationFormValues {
  const observabilityTool = OBSERVABILITY_TOOLS.find(
    (observabilityTool) =>
      observabilityTool.integrationType === IntegrationType.AWS
  )
  const externalIntegration = getExternalIntegrationForObservabilityTool(
    observabilityTool,
    externalIntegrations
  )

  const domains = getAWSDomains(externalIntegrations)

  const awsDefaultValues: AWSDefaultValues = {
    awsAccessKeyID: '',
    awsIamRoleArn: '',
    awsSecretAccessKey: '',
    awsAuthenticationType: AWSAuthenticationType.IAMRole,
    awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
    domain: { name: '', integrationType: IntegrationType.AWS },
    externalIntegration: externalIntegration,
    observabilityTool: {
      name: 'AWS',
      type: ObservabilityToolType.AWS,
      integrationType: IntegrationType.AWS,
      domains,
      selectedDomain: ''
    }
  }

  const defaultValues =
    getAWSOnboardingAuthenticationDefaultValues(externalIntegrations)

  return {
    ...defaultValues,
    ...awsDefaultValues
  }
}
